import { getAuth } from "firebase/auth";

import store from "../../redux/store";
import { setUserToken } from "../../redux/slices/userSlice";
import { server } from "../../config/ConfigFirebase";
import { debugLog } from "../../functions";

// Solicitudes de pago de contratos con persona jurídica (colección
// paymentRequestsLegal). Patrón ContractLegalService.js: todo pasa por la Cloud
// Function handlePaymentRequestLegal con Bearer token; el cliente nunca toca la
// colección directamente.
//
// El entityNit de la empresa NO viaja en el body: lo resuelve el servidor desde
// users/{uid}. Cualquier intento de mandarlo se ignora.

const handlePaymentRequestLegal = async (token, body) => {
  const response = await fetch(
    "https://" + server + ".cloudfunctions.net/handlePaymentRequestLegal",
    {
      method: "post",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
    }
  );

  if (response.status === 401) {
    const auth = getAuth();
    const token = await auth.currentUser.getIdToken();
    store.dispatch(setUserToken(token));
  }

  return response;
};

// Convierte las fechas serializadas a Date. Las fechas de la factura
// (invoiceIssuedAt, invoiceDueAt) y las del recorrido financiero (financial.*)
// NO se tocan: son fechas de calendario ISO / ISO strings y se leen tal cual.
const withDates = (request) =>
  request && {
    ...request,
    createdAt: request.createdAt ? new Date(request.createdAt) : null,
    updatedAt: request.updatedAt ? new Date(request.updatedAt) : null,
    submittedAt: request.submittedAt ? new Date(request.submittedAt) : null,
  };

// Los errores de negocio del backend (documento faltante, RUT vencido, valor
// inválido) traen un `message` en español pensado para mostrarse tal cual. Se
// devuelve {ok:false, message} en vez de null para no perderlo, a diferencia de
// las lecturas, donde null basta.
const asResult = async (response) => {
  const data = await response.json();
  if (data.result) return { ok: true, data: data.body };
  return { ok: false, error: data.error, message: data.message || null };
};

export const createPaymentRequest = async (contractCode, user) => {
  try {
    const body = { action: "CREATE_PAYMENT_REQUEST", contractCode };
    const response = await handlePaymentRequestLegal(user.token, body);
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

export const getPaymentRequest = async (requestId, user) => {
  try {
    const body = { action: "GET_PAYMENT_REQUEST", requestId };
    let response = await handlePaymentRequestLegal(user.token, body);
    response = await response.json();
    return response.result ? withDates(response.body) : null;
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const getPaymentRequests = async (contractCode, user) => {
  try {
    const body = { action: "GET_PAYMENT_REQUESTS", contractCode };
    let response = await handlePaymentRequestLegal(user.token, body);
    response = await response.json();
    return response.result ? response.body.map(withDates) : null;
  } catch (error) {
    debugLog(error);
    return null;
  }
};

export const updatePaymentRequest = async (requestId, data, user) => {
  try {
    const body = { action: "UPDATE_PAYMENT_REQUEST", requestId, data };
    const response = await handlePaymentRequestLegal(user.token, body);
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

export const deletePaymentRequest = async (requestId, user) => {
  try {
    const body = { action: "DELETE_PAYMENT_REQUEST", requestId };
    const response = await handlePaymentRequestLegal(user.token, body);
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

// El backend revalida la completitud y la vigencia del RUT: si devuelve
// {ok:false}, `message` explica exactamente qué falta.
export const submitPaymentRequest = async (requestId, user) => {
  try {
    const body = { action: "SUBMIT_PAYMENT_REQUEST", requestId };
    const response = await handlePaymentRequestLegal(user.token, body);
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

// No hay actualización de documentos de la entidad por aquí: el RUT vencido se
// reemplaza con `uploadEntityDocument` de EntityService (handleEntity,
// ADD_DOCUMENT con replaceFileId), que conserva versions[] y deja bitacora en la
// entidad. Después de eso, la solicitud RELEE el documento con
// updatePaymentRequest sobre la clave devuelta. Ver entity-docs-real-source, D2.

// --- Revisión por el supervisor (C7) ----------------------------------------
// Todas comparten forma: el backend es la autoridad y devuelve la solicitud ya
// actualizada, así que la vista se refresca con lo que respondió y no con lo
// que creía tener.

const reviewAction = async (action, payload, user) => {
  try {
    const response = await handlePaymentRequestLegal(user.token, { action, ...payload });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

// Marca un documento como revisado.
export const reviewDocument = (requestId, key, user) =>
  reviewAction("REVIEW_DOCUMENT", { requestId, key }, user);

// Devuelve un documento. La observación es obligatoria: el backend rechaza la
// petición sin ella, y es lo único que la empresa va a leer para saber qué
// rehacer.
export const returnDocument = (requestId, key, message, user) =>
  reviewAction("RETURN_DOCUMENT", { requestId, key, message }, user);

// Deshace una devolución. Solo funciona mientras no se haya notificado.
export const undoReturnDocument = (requestId, key, user) =>
  reviewAction("UNDO_RETURN_DOCUMENT", { requestId, key }, user);

// Cierra la revisión: pasa la solicitud a "En corrección" y manda UN correo con
// todos los documentos devueltos.
export const notifyReturns = (requestId, user, origin = window.location.origin) =>
  reviewAction("NOTIFY_RETURNS", { requestId, origin }, user);

// La empresa cierra su ciclo de corrección y la solicitud vuelve a revisión.
export const notifyCorrection = (requestId, user, origin = window.location.origin) =>
  reviewAction("NOTIFY_CORRECTION", { requestId, origin }, user);

// Aprueba la solicitud. El backend revalida que todo lo obligatorio esté
// revisado y que nada quede devuelto.
export const approvePaymentRequest = (requestId, user, origin = window.location.origin) =>
  reviewAction("APPROVE_PAYMENT_REQUEST", { requestId, origin }, user);

// Regenera los documentos de supervisión (Informe FO-GBS-01 y Acta FO-GAD-10)
// de una solicitud ya aprobada, como un juego. Cada llamada crea una versión
// nueva; solo el supervisor efectivo o ADMIN (el backend lo revalida).
export const generatePaymentDocuments = (requestId, user) =>
  reviewAction("GENERATE_PAYMENT_DOCUMENTS", { requestId }, user);

// Bandeja del supervisor: las solicitudes por revisar de TODOS los contratos
// que supervisa. Sin parámetros de alcance — el backend lo resuelve desde el
// documento del actor y las novedades del contrato, así que no hay nada que
// manipular desde aquí.
export const getPaymentRequestsToReview = async (user) => {
  try {
    const response = await handlePaymentRequestLegal(user.token, {
      action: "GET_PAYMENT_REQUESTS",
    });
    return await asResult(response);
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

// --- Recorrido financiero (legal-payment-financial-flow) ---------------------
// Causación → Contabilidad → Presupuesto → Tesorería → Caja. Tres acciones para
// seis etapas: el servidor resuelve, por el estado ACTUAL de la solicitud y la
// tabla PAYMENT_STAGES, quién puede actuar y adónde va. Ninguna manda correo.

// Bandeja de Gestión de Pagos: solicitudes en recorrido financiero de TODOS los
// contratos, enriquecidas con el contrato y su ejecución. `includePaid` agrega
// las pagadas del año; `requestId` pide una sola (detalle) con el mismo
// enriquecimiento.
export const getFinancialPaymentRequests = async (user, { includePaid = false, requestId = null } = {}) => {
  try {
    const body = { action: "GET_FINANCIAL_PAYMENT_REQUESTS", includePaid };
    if (requestId) body.requestId = requestId;
    const response = await handlePaymentRequestLegal(user.token, body);
    const result = await asResult(response);
    return result.ok ? { ...result, data: (result.data || []).map(withDates) } : result;
  } catch (error) {
    debugLog(error);
    return { ok: false, error: "NETWORK" };
  }
};

// Causación recibe una solicitud aprobada por el supervisor.
export const receivePaymentRequest = (requestId, user) =>
  reviewAction("RECEIVE_PAYMENT_REQUEST", { requestId }, user);

// Avanza a la siguiente etapa. `data` es opcional: Tesorería puede mandar
// { paidAt, reference }, Caja { proof }; el resto va vacío.
export const advancePaymentRequest = (requestId, data, user) =>
  reviewAction("ADVANCE_PAYMENT_REQUEST", { requestId, data: data || {} }, user);

// Devuelve a Causación con motivo obligatorio (Contabilidad, Presupuesto y
// Tesorería). El servidor rechaza el motivo vacío.
export const returnPaymentRequestToCausation = (requestId, message, user) =>
  reviewAction("RETURN_PAYMENT_REQUEST", { requestId, data: { message } }, user);

// Mensaje en español para un {ok:false} del recorrido financiero. BLOCKED trae
// el motivo exacto del servidor (la misma regla que deshabilita el botón); los
// demás códigos se traducen aquí.
const PAYMENT_FLOW_ERROR_LABELS = {
  FORBIDDEN: "No tienes permiso para actuar en la etapa actual de esta solicitud",
  INVALID_TRANSITION: "La solicitud cambió de estado. Recarga la página para ver su estado actual",
  NOT_FOUND: "La solicitud no existe o ya no está disponible",
  NETWORK: "No fue posible comunicarse con el servidor",
};

export const paymentFlowErrorMessage = (result) => {
  if (!result || result.ok) return "";
  if (result.message) return result.message;
  return PAYMENT_FLOW_ERROR_LABELS[result.error] || "No fue posible completar la acción";
};
